import { useMemo, useState } from 'react'
import { Box, Stack, TableBody, TableHead, TableRow, useTheme } from '@mui/material'

import { COUNTRY_NAME_BY_A3, resolveCountryCode } from '@repo/types'
import {
  Card,
  Map,
  Skeleton,
  WORLD_FRAME_ASPECT_RATIO,
  type MapChoroplethConfig,
  type MapItem,
} from '@repo/ui'
import { GOOGLE_MAPS_API_KEY } from 'config'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { ViewToggle, type ViewToggleOption } from 'components/input/ViewToggle/ViewToggle'
import { useWebsiteLive } from 'hooks/api'
import type { WebsiteAnalyticsOverview, WebsiteLiveResponse } from 'services'

import { AUDIENCE_LABELS, formatCount, type Audience } from '../websiteAnalyticsModel'
import { Cell, EmptyRow, HeadCell, SectionNote, SourceNotices, StatTable } from './shared'
import { LIVE_NOW_ANCHOR_ID, LiveNowKpi } from './WebsiteLiveCards'

const OPTIONS: readonly ViewToggleOption<Audience>[] = [
  { value: 'all', label: 'Countries' },
  { value: 'consented', label: 'Cities' },
]

const MAP_HEIGHT = 420

/**
 * VRG-640 — where visitors are. Reuses `@repo/ui`'s `Map` (the VERA-260 scan map) in its
 * two existing modes rather than a new map: the COUNTRY choropleth that
 * `ScansByCountry` drives for all visitors (Vercel gives countries only), and PINS for
 * consenting visitors' cities (PostHog's GeoIP). Country codes go through the same
 * `resolveCountryCode` the scan map uses, so an unmatched code is counted, not dropped.
 */
/**
 * Live sessions grouped by place, for the live map. Sessions without a GeoIP point are
 * counted in the table (as their city/country) but cannot be pinned.
 */
const livePlaces = (live: WebsiteLiveResponse | undefined) => {
  const byPlace = new globalThis.Map<
    string,
    { key: string; label: string; lat: number | null; lng: number | null; visitors: number }
  >()
  for (const s of live?.sessions ?? []) {
    const key = `${s.country ?? ''}-${s.city ?? ''}`
    const cur = byPlace.get(key) ?? {
      key,
      label: `${s.city || 'Unknown city'}, ${s.country || '—'}`,
      lat: s.lat,
      lng: s.lng,
      visitors: 0,
    }
    cur.visitors += 1
    if (cur.lat === null && s.lat !== null) {
      cur.lat = s.lat
      cur.lng = s.lng
    }
    byPlace.set(key, cur)
  }
  return [...byPlace.values()]
}

export const WebsiteMapCard = ({ overview }: { overview: WebsiteAnalyticsOverview }) => {
  const theme = useTheme()
  const mapStyle = theme.palette.mode === 'dark' ? 'dark' : 'light'
  const [view, setView] = useState<Audience>('all')
  const { countries, cities } = overview.map

  const choropleth = useMemo<MapChoroplethConfig>(() => {
    let unmatched = 0
    const data = countries.flatMap((c) => {
      const a3 = resolveCountryCode(c.country)
      if (!a3) {
        unmatched += c.visitors
        return []
      }
      return [
        {
          code: a3,
          name: COUNTRY_NAME_BY_A3[a3] ?? c.country,
          value: c.visitors,
          numerator: c.visitors,
          denominator: c.visitors,
        },
      ]
    })
    return {
      data,
      kind: 'count',
      label: `Visitors: ${AUDIENCE_LABELS.all}`,
      footnote: unmatched
        ? `${unmatched} visitor${unmatched === 1 ? '' : 's'} from a country code the map could not place.`
        : undefined,
    }
  }, [countries])

  const pins = useMemo<MapItem[]>(
    () =>
      cities
        .filter((c) => c.lat !== null && c.lng !== null)
        .map((c) => ({
          id: `${c.country}-${c.city}`,
          lat: c.lat as number,
          lng: c.lng as number,
          title: `${c.city || 'Unknown city'}, ${c.country}: ${c.visitors} visitor${c.visitors === 1 ? '' : 's'}`,
        })),
    [cities],
  )

  const rows =
    view === 'all'
      ? countries.map((c) => ({ key: c.country, label: c.country, visitors: c.visitors }))
      : cities.map((c) => ({
          key: `${c.country}-${c.city}`,
          label: `${c.city || 'Unknown city'}, ${c.country || '—'}`,
          visitors: c.visitors,
        }))

  return (
    <Card
      title="Map by range"
      action={<ViewToggle options={OPTIONS} value={view} onChange={setView} ariaLabel="Map level" />}
    >
      <Stack spacing={3} sx={{ mt: 4 }}>
        <SectionNote>
          {view === 'all'
            ? `Countries: ${AUDIENCE_LABELS.all}.`
            : `Cities: ${AUDIENCE_LABELS.consented} only (PostHog GeoIP).`}
        </SectionNote>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 4 }}>
          <Box>
            {view === 'all' ? (
              <Map
                items={[]}
                apiKey={GOOGLE_MAPS_API_KEY || ''}
                mapStyle={mapStyle}
                height={MAP_HEIGHT}
                renderMode="choropleth"
                choropleth={choropleth}
                emptyMessage="No visitors in this range."
              />
            ) : (
              <Map
                items={pins}
                apiKey={GOOGLE_MAPS_API_KEY || ''}
                mapStyle={mapStyle}
                height={MAP_HEIGHT}
                enableClustering
                emptyMessage="No located visitors in this range."
              />
            )}
          </Box>
          <StatTable>
            <TableHead>
              <TableRow>
                <HeadCell>{view === 'all' ? 'Country' : 'City'}</HeadCell>
                <HeadCell align="right">Visitors</HeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <EmptyRow colSpan={2} text="Nothing to place for this range." />
              ) : (
                rows
                  .slice()
                  .sort((a, b) => b.visitors - a.visitors)
                  .slice(0, 15)
                  .map((r) => (
                    <TableRow key={r.key}>
                      <Cell>{r.label}</Cell>
                      <Cell align="right">{formatCount(r.visitors)}</Cell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </StatTable>
        </Box>
      </Stack>
    </Card>
  )
}

/**
 * VRG-689 (Spencer, 2026-10-13) — the TOP of Analytics → Website: who is on vera.inc right
 * now, on a map, with the "N on the site now" figure beside it. Replaces the Live toggle,
 * which hid this behind a switch and swapped the range map out for it.
 *
 * Same data as before (`useWebsiteLive`: PostHog sessions active in the last 5 minutes,
 * one react-query entry shared with the Live now list), so only visitors with analytics on
 * can be placed — the tooltip says so. Polls every 30 s; react-query does not refetch on an
 * interval while the tab is hidden (`refetchIntervalInBackground: false`, set on the hook).
 * Empty is "No one on the site right now", never a blank map; loading is a skeleton of the
 * map and the city list (§19).
 */
export const WebsiteLiveMapCard = () => {
  const theme = useTheme()
  const mapStyle = theme.palette.mode === 'dark' ? 'dark' : 'light'
  const { data: liveData, isLoading, error } = useWebsiteLive()
  const liveError = !!error || liveData?.posthog.status === 'error'
  const places = useMemo(() => livePlaces(liveData), [liveData])
  const livePins = useMemo<MapItem[]>(
    () =>
      places
        .filter((p) => p.lat !== null && p.lng !== null)
        .map((p) => ({
          id: `live-${p.key}`,
          lat: p.lat as number,
          lng: p.lng as number,
          fillColor: theme.palette.green[600],
          title: `${p.label}: ${p.visitors} on the site now`,
        })),
    [places, theme],
  )
  const empty = liveError ? 'Live visitors could not be loaded.' : 'No one on the site right now'

  return (
    <Box data-testid="website-live-map">
    <Card
      title="Live map"
      action={
        <Stack direction="row" alignItems="center" spacing={2}>
          {/* The consent caveat lives here (Spencer): only analytics-on visitors can be placed. */}
          <InfoTooltip term="website-live-map" />
          <LiveNowKpi
            onClick={() =>
              document.getElementById(LIVE_NOW_ANCHOR_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          />
        </Stack>
      }
    >
      <Stack spacing={3} sx={{ mt: 4 }}>
        {liveData ? <SourceNotices sources={[['PostHog', liveData.posthog]]} /> : null}
        {/*
          The city list's track never goes below the table's own width (VRG-699). With
          `minmax(0, 1fr)` it was 207px at a 1024 viewport against a 225px table, so the
          StatTable scrolled and its last column's padding sat under the clip: the counts ran
          flush to the card edge. At 1280 and up 1fr is already wider than the table, so
          nothing moves there; at 1024 the map gives up the 18px.
        */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 2fr) minmax(min-content, 1fr)' },
            gap: 4,
          }}
        >
          {isLoading ? (
            <>
              {/* Same footprint as the map (§19): the world's ratio, not a fixed height. */}
              <Box sx={{ aspectRatio: String(WORLD_FRAME_ASPECT_RATIO) }}>
                <Skeleton height="100%" sx={{ borderRadius: 2 }} />
              </Box>
              <Stack spacing={2}>
                <Skeleton height={14} lines={6} />
              </Stack>
            </>
          ) : (
            <>
              <Box>
                <Map
                  items={livePins}
                  apiKey={GOOGLE_MAPS_API_KEY || ''}
                  mapStyle={mapStyle}
                  // VRG-699 — the whole world, ~80°N to 60°S, at its natural ratio: the
                  // height follows the width. It replaced a fixed 320px with `fitToItems`,
                  // which cropped to the northern band (and, with one visitor, to a city).
                  // Every live visitor is on screen without a fit, because the frame is
                  // the world.
                  frameWorld
                  // One pin per live city: a "+2" cluster would hide which cities are live.
                  enableClustering={false}
                  pulseItems
                  emptyMessage={empty}
                />
              </Box>
              <StatTable>
                <TableHead>
                  <TableRow>
                    <HeadCell>City</HeadCell>
                    <HeadCell align="right">On the site now</HeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {places.length === 0 ? (
                    <EmptyRow colSpan={2} text={empty} />
                  ) : (
                    places
                      .slice()
                      .sort((a, b) => b.visitors - a.visitors)
                      .slice(0, 10)
                      .map((p) => (
                        <TableRow key={p.key}>
                          <Cell>{p.label}</Cell>
                          <Cell align="right">{formatCount(p.visitors)}</Cell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </StatTable>
            </>
          )}
        </Box>
      </Stack>
    </Card>
    </Box>
  )
}
